import { COLORS, REQUEST_STATUS } from "@src/constants";
import { ResourcePlanDashboard } from "@src/features/resource_plan/components/resource_plan_dashboard";
import {
  MonthlyTotalFte,
  RequestStatisticsSettings,
  ResourcePlanRequestStatus,
} from "@src/types/dashboard_types";
import { Allocation, RoleRequest } from "@src/types/role_request_types";
import dayjs from "dayjs";
import _ from "lodash";

/**
 * Generates chart settings for the resource plan dashboard statistics.
 *
 * @param requestStatusTotals The totals of every status for the resource plan dashboard.
 * @returns The settings for the chart including labels, colors, and data.
 */
export function getResourcePlanChartSettings(
  requestStatusTotals: ResourcePlanRequestStatus
): RequestStatisticsSettings {
  const labels: string[] = [
    requestStatusTotals?.inProgress > 0
      ? REQUEST_STATUS.REQUESTED + " / " + REQUEST_STATUS.IN_PROGRESS
      : REQUEST_STATUS.REQUESTED,
    REQUEST_STATUS.PROPOSED,
    REQUEST_STATUS.CONFIRMED,
    "Outdated",
    _.startCase(REQUEST_STATUS.IN_RECRUITMENT),
    "Declined (by team lead)",
  ];

  const colors: string[] = [
    COLORS.BRAND_60,
    COLORS.ENERGY_BLUE_40,
    COLORS.SUCCESS,
    COLORS.ORANGE_50,
    COLORS.YELLOW_30,
    COLORS.DARK_RED_50,
  ];

  const data: number[] = [
    requestStatusTotals?.requested,
    requestStatusTotals?.proposed,
    requestStatusTotals?.confirmed,
    requestStatusTotals?.outdated,
    requestStatusTotals?.inRecruitment,
    requestStatusTotals?.declined,
  ];

  return {
    labels: labels,
    colors: colors,
    data: data,
    total: data.reduce(
      (sum: number, nextNumber: number) => sum + nextNumber,
      0
    ),
  };
}

/**
 * Calculates the resource plan dashboard data based on role request data.
 * @returns An object containing request status totals and total FTE for each month.
 */
export function calculateResourcePlanDashboardData(
  roleRequestData: RoleRequest[]
): ResourcePlanDashboard {
  const requestStatusTotals: ResourcePlanRequestStatus = {
    requested: 0,
    proposed: 0,
    confirmed: 0,
    inRecruitment: 0,
    declined: 0,
    requiredAction: 0,
    declinedTag: 0,
    outdated: 0,
    total: 0,
    replied: 0,
    newComments: 0,
    inProgress: 0,
  };

  const statusMap: Record<string, keyof typeof requestStatusTotals> = {
    Requested: "requested",
    Proposed: "proposed",
    Confirmed: "confirmed",
    "In recruitment": "inRecruitment",
    Declined: "declined",
    "In progress": "inProgress",
  };

  for (const roleRequest of roleRequestData) {
    const {
      actionRequired,
      isOutdated,
      status,
      isDeclined,
      isCommentAvailable,
      isCommentRead,
    } = roleRequest;

    if (actionRequired) requestStatusTotals.requiredAction++;
    if (isCommentAvailable && !isCommentRead) requestStatusTotals.newComments++;

    // if the request is outdated, it should be counted in the outdated count and the total count should be increased
    if (isOutdated && status !== REQUEST_STATUS.DRAFT) {
      requestStatusTotals.outdated++;
    }
    if (isDeclined) requestStatusTotals.declinedTag++;

    if (status === REQUEST_STATUS.IN_PROGRESS) {
      requestStatusTotals.requested++;
    }

    // should only count the status request for eg "Requested" or "Proposed" if it is not outdated
    // otherwise it will be counted twice in the outdated count and in the status count
    const statusKey = statusMap[status as string];
    if (statusKey && !isOutdated) {
      requestStatusTotals[statusKey]++;
    }
  }

  return {
    requestStatusTotals,
    totalFTE: calculateTotalFTE(roleRequestData),
  };
}

/**
 * Calculates the total FTE for each month based on the role request data.
 * @returns An array of MonthlyTotalFte objects, each containing the month, year, and total FTE for that month.
 */
function calculateTotalFTE(roleRequestData: RoleRequest[]): MonthlyTotalFte[] {
  const allocations: Allocation[] = roleRequestData
    .filter((request: RoleRequest) => request.status !== REQUEST_STATUS.DRAFT)
    .flatMap((request: RoleRequest) => request.allocationDetails);

  const fteByMonth = new Map<string, MonthlyTotalFte>();

  for (const { date, requiredPercentage } of allocations) {
    const month: number = dayjs(date).month();
    const year: number = dayjs(date).year();
    const key = `${year}-${month}`;

    const foundMonthlyTotalFTE: MonthlyTotalFte = fteByMonth.get(key);

    if (foundMonthlyTotalFTE) {
      foundMonthlyTotalFTE.totalFte += requiredPercentage / 100;
    } else {
      fteByMonth.set(key, {
        month,
        year,
        totalFte: requiredPercentage / 100,
      });
    }
  }

  return Array.from(fteByMonth.values()).sort(
    (previousMonthlyFTE: MonthlyTotalFte, nexMonthlyFTE: MonthlyTotalFte) => {
      return (
        previousMonthlyFTE.year - nexMonthlyFTE.year ||
        previousMonthlyFTE.month - nexMonthlyFTE.month
      );
    }
  );
}
